/**
 * ============================================================================
 * CategorySection — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: a single collapsible block within the Impact Set review. Inside
 *          the block we render a borderless table of the impact items with
 *          confidence badge + discovery rule + rationale.
 *
 * Per CCIE-CONF-001 the sections holding MEDIUM/LOW confidence rows are
 * collapsed by default; the parent flips them open via the prop.
 *
 * A11Y:
 *   - <details>/<summary> gives free keyboard + SR support; we add aria-label
 *     to <summary> so the section's identity is announced even when
 *     collapsed.
 *   - <table> with <caption> and <th scope="col"> per the screens spec.
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ImpactSetReview.module.css';
import { ConfidenceBadge } from './ConfidenceBadge';
import type { ImpactCategory, ImpactItem } from './useImpactSetQuery';

export interface CategorySectionProps {
  category: ImpactCategory;
  items: ImpactItem[];
  /** Defaults the <details> open attribute. The parent decides. */
  defaultOpen: boolean;
}

export function CategorySection({ category, items, defaultOpen }: CategorySectionProps) {
  const { t, i18n } = useTranslation();
  const nf = new Intl.NumberFormat(i18n.language);

  const label = t(`changeControl.impactSet.category.${category}.label`, {
    defaultValue: category,
  });

  return (
    <details
      className={styles.categoryDetails}
      open={defaultOpen || undefined}
    >
      <summary
        className={styles.categorySummary}
        aria-label={(t as (k: string, o?: Record<string, unknown>) => string)(
          'changeControl.impactSet.categorySummaryAria',
          {
            defaultValue: '{{category}} — {{count}} impacted',
            category: label,
            count: nf.format(items.length),
          },
        )}
      >
        <span className={styles.categoryLabel}>{label}</span>
        <span className={styles.categoryBadge} aria-hidden="true">
          {nf.format(items.length)}
        </span>
      </summary>

      <div className={styles.categoryBody}>
        {items.length === 0 ? (
          <p className={styles.emptyCat}>
            {t('changeControl.impactSet.categoryEmpty', {
              defaultValue: 'No impacted entities in this category.',
            })}
          </p>
        ) : (
          <table
            className={`${wstyles.table} ${styles.itemTable}`}
            aria-label={t('changeControl.impactSet.itemTableAria', {
              defaultValue: '{{category}} impact items',
              category: label,
            })}
          >
            <caption className={styles.srOnly}>
              {t('changeControl.impactSet.itemTableCaption', {
                defaultValue:
                  'Impact items in the {{category}} category, with confidence, discovery rule, and rationale.',
                category: label,
              })}
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  {t('changeControl.impactSet.columns.entity', {
                    defaultValue: 'Entity',
                  })}
                </th>
                <th scope="col">
                  {t('changeControl.impactSet.columns.confidence', {
                    defaultValue: 'Confidence',
                  })}
                </th>
                <th scope="col">
                  {t('changeControl.impactSet.columns.rule', {
                    defaultValue: 'Discovery rule',
                  })}
                </th>
                <th scope="col">
                  {t('changeControl.impactSet.columns.rationale', {
                    defaultValue: 'Rationale',
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.item_id}>
                  <td>
                    <div className={styles.entityCell}>
                      <span className={styles.entityKind} aria-hidden="true">
                        {it.entity_kind}
                      </span>
                      <strong>{it.entity_display_name}</strong>
                    </div>
                  </td>
                  <td>
                    <ConfidenceBadge confidence={it.confidence} />
                  </td>
                  <td>
                    <code className={styles.ruleCode}>{it.discovery_rule}</code>
                  </td>
                  <td className={styles.rationaleCell}>{it.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
}
